import React, { useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  Paper,
  Grid,
  Alert
} from '@mui/material';
import instance from '../utils/axiosConfig';

const capitalizeWords = (str) => {
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

const PlanEditor = ({ plan, onSave }) => {

  const [formData, setFormData] = useState({
    // Basic plan info from MongoDB
    title: plan?.title.toLowerCase() || '',
    price: plan?.price || 0,
    features: plan?.features || [],
    notIncluded: plan?.notIncluded || [],
    isPopular: plan?.isPopular || false,

    // MP Plan structure
    reason: plan?.mpPlanDetails?.reason || `Plan ${capitalizeWords(plan?.title) || ''}`,
    auto_recurring: {
      frequency: plan?.mpPlanDetails?.auto_recurring?.frequency || 1,
      frequency_type: plan?.mpPlanDetails?.auto_recurring?.frequency_type || 'months',
      repetitions: plan?.mpPlanDetails?.auto_recurring?.repetitions || 12,
      billing_day: plan?.mpPlanDetails?.auto_recurring?.billing_day || 1,
      billing_day_proportional: plan?.mpPlanDetails?.auto_recurring?.billing_day_proportional ?? true,
      free_trial: plan?.mpPlanDetails?.auto_recurring?.free_trial || {
        frequency: 0,
        frequency_type: 'months'
      },
      transaction_amount: plan?.price || 0,
      currency_id: plan?.mpPlanDetails?.auto_recurring?.currency_id || 'ARS'
    },
    payment_methods_allowed: {
      payment_types: plan?.mpPlanDetails?.payment_methods_allowed?.payment_types || [{ id: "credit_card" }],
      payment_methods: plan?.mpPlanDetails?.payment_methods_allowed?.payment_methods || [
        { id: "visa" },
        { id: "mastercard" },
        { id: "amex" }
      ]
    },
    back_url: plan?.mpPlanDetails?.back_url || process.env.FRONTEND_URL
  });

  const [includeFreeTrial, setIncludeFreeTrial] = useState(
    plan?.mpPlanDetails?.auto_recurring?.free_trial?.frequency > 0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create base auto_recurring object
      const auto_recurring = {
        frequency: formData.auto_recurring.frequency,
        frequency_type: formData.auto_recurring.frequency_type,
        repetitions: formData.auto_recurring.repetitions,
        billing_day: formData.auto_recurring.billing_day,
        billing_day_proportional: formData.auto_recurring.billing_day_proportional,
        transaction_amount: formData.price,
        currency_id: formData.auto_recurring.currency_id,
        // Always include free_trial with at least frequency_type
        free_trial: includeFreeTrial ? 
          formData.auto_recurring.free_trial : 
          { frequency_type: 'days' }
      };

      const mpPlanData = {
        reason: capitalizeWords(formData.reason),
        auto_recurring,
        payment_methods_allowed: formData.payment_methods_allowed,
        back_url: formData.back_url
      };


      const response = await instance.put(`/api/mp/edit-plan/${plan._id}`, {
        title: formData.title,
        price: formData.price,
        features: formData.features,
        notIncluded: formData.notIncluded,
        isPopular: formData.isPopular,
        mpPlanData
      });

      const data = response.data;
      
      if (data.success) {
        setSuccess('Plan actualizado exitosamente');
        setError('');
        if (onSave) onSave(data.plan);
      } else {
        setError(data.error || 'Error al actualizar el plan');
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      setError('Error al actualizar el plan');
    }
  };

  // Available payment types and methods
  const availablePaymentTypes = [
    { id: "credit_card", name: "Tarjeta de Crédito" },
    { id: "debit_card", name: "Tarjeta de Débito" }
  ];

  const availablePaymentMethods = [
    { id: "visa", name: "Visa" },
    { id: "mastercard", name: "Mastercard" },
    { id: "amex", name: "American Express" }
  ];

  // Handler for payment methods
  const handlePaymentMethodToggle = (methodId, type) => {
    setFormData(prev => ({
      ...prev,
      payment_methods_allowed: {
        ...prev.payment_methods_allowed,
        [type]: prev.payment_methods_allowed[type].some(m => m.id === methodId)
          ? prev.payment_methods_allowed[type].filter(m => m.id !== methodId)
          : [...prev.payment_methods_allowed[type], { id: methodId }]
      }
    }));
  };

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleAutoRecurringChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      auto_recurring: {
        ...prev.auto_recurring,
        [field]: value
      }
    }));
  };

  const handleFreeTrialChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      auto_recurring: {
        ...prev.auto_recurring,
        free_trial: {
          ...prev.auto_recurring.free_trial,
          [field]: value
        }
      }
    }));
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Editar Plan</Typography>
      
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Plan Information */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Nombre de plan (siempre minúsculas)"
              label="Título"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Precio"
              type="number"
              value={formData.price}
              onChange={(e) => {
                const price = parseFloat(e.target.value);
                setFormData(prev => ({
                  ...prev,
                  price,
                  auto_recurring: {
                    ...prev.auto_recurring,
                    transaction_amount: price
                  }
                }));
              }}
            />
          </Grid>

          {/* Auto Recurring Configuration */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Configuración de Recurrencia</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Frecuencia"
                  type="number"
                  value={formData.auto_recurring.frequency}
                  onChange={(e) => handleAutoRecurringChange('frequency', parseInt(e.target.value))}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Día de Cobro"
                  type="number"
                  value={formData.auto_recurring.billing_day}
                  onChange={(e) => handleAutoRecurringChange('billing_day', parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 28 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Repeticiones"
                  type="number"
                  value={formData.auto_recurring.repetitions}
                  onChange={(e) => handleAutoRecurringChange('repetitions', parseInt(e.target.value))}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.auto_recurring.billing_day_proportional}
                      onChange={(e) => handleAutoRecurringChange('billing_day_proportional', e.target.checked)}
                    />
                  }
                  label="Cobro Proporcional"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Frecuencia"
                  type="number"
                  value={formData.auto_recurring.frequency}
                  onChange={(e) => handleAutoRecurringChange('frequency', parseInt(e.target.value))}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Frecuencia</InputLabel>
                  <Select
                    value={formData.auto_recurring.frequency_type}
                    onChange={(e) => handleAutoRecurringChange('frequency_type', e.target.value)}
                    label="Tipo de Frecuencia"
                  >
                    <MenuItem value="days">Días</MenuItem>
                    <MenuItem value="months">Meses</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          {/* Free Trial Configuration */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Período de Prueba</Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={includeFreeTrial}
                  onChange={(e) => setIncludeFreeTrial(e.target.checked)}
                />
              }
              label="Incluir período de prueba"
            />

            {includeFreeTrial && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Duración"
                    type="number"
                    value={formData.auto_recurring.free_trial.frequency}
                    onChange={(e) => handleFreeTrialChange('frequency', parseInt(e.target.value))}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Duración</InputLabel>
                    <Select
                      value={formData.auto_recurring.free_trial.frequency_type}
                      onChange={(e) => handleFreeTrialChange('frequency_type', e.target.value)}
                      label="Tipo de Duración"
                    >
                      <MenuItem value="days">Días</MenuItem>
                      <MenuItem value="months">Meses</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Grid>

          {/* Status and Other Info */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPopular}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPopular: e.target.checked }))}
                />
              }
              label="Plan Popular"
            />
          </Grid>

          {/* Payment Methods Configuration */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Métodos de Pago</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Tipos de Pago</Typography>
                {availablePaymentTypes.map(type => (
                  <FormControlLabel
                    key={type.id}
                    control={
                      <Switch
                        checked={formData.payment_methods_allowed.payment_types.some(t => t.id === type.id)}
                        onChange={() => handlePaymentMethodToggle(type.id, 'payment_types')}
                      />
                    }
                    label={type.name}
                  />
                ))}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1">Tarjetas Aceptadas</Typography>
                {availablePaymentMethods.map(method => (
                  <FormControlLabel
                    key={method.id}
                    control={
                      <Switch
                        checked={formData.payment_methods_allowed.payment_methods.some(m => m.id === method.id)}
                        onChange={() => handlePaymentMethodToggle(method.id, 'payment_methods')}
                      />
                    }
                    label={method.name}
                  />
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* Currency Configuration */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Moneda</InputLabel>
              <Select
                value={formData.auto_recurring.currency_id}
                onChange={(e) => handleAutoRecurringChange('currency_id', e.target.value)}
                label="Moneda"
              >
                <MenuItem value="ARS">Peso Argentino (ARS)</MenuItem>
                <MenuItem value="USD">Dólar Estadounidense (USD)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Reason Field */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Razón del Plan"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              helperText="Descripción que verá el usuario"
            />
          </Grid>

          {/* Back URL */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URL de Retorno"
              value={formData.back_url}
              onChange={(e) => setFormData(prev => ({ ...prev, back_url: e.target.value }))}
              helperText="URL a la que volverá el usuario después del pago"
            />
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
            >
              Guardar Cambios
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default PlanEditor; 